import { getAuth } from 'firebase/auth';
import { messagingGetWithAuth, messagingPostWithAuth } from './hybridApiClient';

/**
 * Web Push (VAPID) — הודעות שמגיעות גם כשהטאב/הדפדפן סגורים לגמרי.
 * שונה מ-messageNotificationService (שמטפל בצליל/Notification בזמן שהאפליקציה פתוחה):
 * כאן זו הרשמה חד-פעמית לכל מכשיר, שנשמרת בשרת ומשמשת לשליחת push גם כשאין דף פתוח.
 */

let cachedVapidKey: string | null = null;
let subscribeInFlight: Promise<boolean> | null = null;

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function isPushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

async function fetchVapidPublicKey(): Promise<string | null> {
  if (cachedVapidKey) return cachedVapidKey;
  try {
    const { data } = await messagingGetWithAuth<{ publicKey?: string }>('/api/push/vapid-public-key', {
      timeout: 10000,
    });
    const key = String(data?.publicKey || '').trim();
    if (!key) return null;
    cachedVapidKey = key;
    return key;
  } catch {
    // השרת עוד לא הוגדר עם VAPID_PUBLIC_KEY/VAPID_PRIVATE_KEY — לא שגיאה קריטית,
    // שאר המערכת (הודעות בזמן אמת כשהאפליקציה פתוחה) ממשיכה לעבוד רגיל.
    return null;
  }
}

/**
 * רישום/רענון Web Push למכשיר הנוכחי — קורא לזה אחרי שהמשתמש אישר הרשאת התראות.
 * בטוח לקרוא שוב ושוב (idempotent): אם כבר יש subscription פעיל הוא רק מוודא שהשרת מעודכן.
 */
export async function ensurePushSubscription(): Promise<boolean> {
  if (!isPushSupported()) return false;
  if (Notification.permission !== 'granted') return false;
  if (subscribeInFlight) return subscribeInFlight;
  subscribeInFlight = (async () => {
    try {
      const uid = String(getAuth().currentUser?.uid || '').trim();
      if (!uid) return false;
      const publicKey = await fetchVapidPublicKey();
      if (!publicKey) return false;
      const registration = await navigator.serviceWorker.ready;
      let subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey) as unknown as BufferSource,
        });
      }
      await messagingPostWithAuth(
        '/api/push/subscribe',
        { subscription: subscription.toJSON() },
        { timeout: 10000 },
      );
      return true;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('[Push] subscribe failed:', error);
      return false;
    } finally {
      subscribeInFlight = null;
    }
  })();
  return subscribeInFlight;
}
